import moment from "moment";
import { PRODUCTION_DATE_FORMAT } from "utilities/oeeDateRange";

/**
 * Additive OEE parts as returned per production day x machine center by the
 * get_oee_summary_by_mc_day SQL function. Every percentage on the OEE Summary
 * report is derived from summed parts (sum first, then divide), so plant and line
 * figures are totals over their scope, never averages of lines or days.
 * - pptSeconds: planned time, uptime + downtime from the Availability summaries;
 * - runTimeSeconds: uptime;
 * - measuredRunTimeSeconds: the part of that uptime in shifts the OEE engine reported.
 */
export interface IOeeParts {
    downtimeSeconds: number;
    earnedSeconds: number;
    goodCount: number;
    measuredRunTimeSeconds: number;
    pptSeconds: number;
    runTimeSeconds: number;
    totalCount: number;
}

export interface IOeeSummaryRow extends IOeeParts {
    machineCenterId: number;
    machineCenterName: string;
    productionDate: string;
    productionLineId: number;
    productionLineName: string;
}

export interface IOeeDowntimeRow {
    downtimeSeconds: number;
    failureTypeId: number;
    machineCenterId: number;
    productionLineId: number;
}

/** Unrounded fractions (1 = 100 %); null when the factor cannot be measured. */
export interface IOeeRatios {
    availability: number | null;
    oee: number | null;
    performance: number | null;
    quality: number | null;
}

export enum OeeMetricEnum {
    OEE = "oee",
    AVAILABILITY = "availability",
    PERFORMANCE = "performance",
    QUALITY = "quality",
}

export type OeeTrendGrain = "day" | "week" | "month";

export interface IOeeTrendPoint {
    bucketStart: string;
    grain: OeeTrendGrain;
    value: number | null;
}

export interface IProductionLineOee {
    parts: IOeeParts;
    productionLineId: number;
    productionLineName: string;
    ratios: IOeeRatios;
}

export interface IMachineCenterLoss {
    lossPercent: number;
    machineCenterId: number;
    machineCenterName: string;
    ratios: IOeeRatios;
}

export interface IDowntimeSegment {
    failureTypeId: number;
    minutes: number;
    percentOfTotal: number;
    seconds: number;
}

export interface IDowntimeBar {
    machineCenterId: number;
    percentOfTotal: number;
    seconds: number;
    segments: IDowntimeSegment[];
}

export interface IDowntimeTopFive {
    bars: IDowntimeBar[];
    totalSeconds: number;
}

export const OEE_TOP_LIMIT = 5;

const DAY_GRAIN_MAX_DAYS = 31;
const WEEK_GRAIN_MAX_DAYS = 182;

const emptyParts = (): IOeeParts => ({
    downtimeSeconds: 0,
    earnedSeconds: 0,
    goodCount: 0,
    measuredRunTimeSeconds: 0,
    pptSeconds: 0,
    runTimeSeconds: 0,
    totalCount: 0,
});

export const sumOeeParts = (rows: readonly IOeeParts[]): IOeeParts =>
    rows.reduce<IOeeParts>(
        (total, row) => ({
            downtimeSeconds: total.downtimeSeconds + row.downtimeSeconds,
            earnedSeconds: total.earnedSeconds + row.earnedSeconds,
            goodCount: total.goodCount + row.goodCount,
            measuredRunTimeSeconds: total.measuredRunTimeSeconds + row.measuredRunTimeSeconds,
            pptSeconds: total.pptSeconds + row.pptSeconds,
            runTimeSeconds: total.runTimeSeconds + row.runTimeSeconds,
            totalCount: total.totalCount + row.totalCount,
        }),
        emptyParts(),
    );

/**
 * OEE = A x P x Q over one scope:
 * - A = uptime / (uptime + downtime), the Availability module's formula;
 * - P = earned time / the uptime of the shifts the OEE engine reported, so shifts
 *   without OEE data never count as lost performance;
 * - no planned time → nothing is measurable (all null);
 * - fully down (run time 0) → A = 0 and OEE = 0, P is null (nothing ran to judge);
 * - uptime but none of it measured → P and OEE are null;
 * - quality without counts → 100 %; performance above 100 % is kept.
 */
export const computeOeeRatios = ({
    earnedSeconds,
    goodCount,
    measuredRunTimeSeconds,
    pptSeconds,
    runTimeSeconds,
    totalCount,
}: IOeeParts): IOeeRatios => {
    if (pptSeconds <= 0) {
        return { availability: null, oee: null, performance: null, quality: null };
    }

    const availability = runTimeSeconds / pptSeconds;
    const quality = totalCount > 0 ? goodCount / totalCount : 1;

    if (runTimeSeconds <= 0) {
        return { availability, oee: 0, performance: null, quality };
    }

    if (measuredRunTimeSeconds <= 0) {
        return { availability, oee: null, performance: null, quality };
    }

    const performance = earnedSeconds / measuredRunTimeSeconds;

    return { availability, oee: availability * performance * quality, performance, quality };
};

export const toWholePercent = (fraction: number | null): number | null =>
    fraction === null ? null : Math.round(fraction * 100);

/** Absorbs floating-point error before rounding down: 0.29 * 100 is 28.999999999999996 in JavaScript. */
const FLOOR_TOLERANCE = 1e-9;

/** Displayed percent of one metric: Availability rounds down like the Availability reports, the rest round. */
export const toMetricPercent = (fraction: number | null, metric: OeeMetricEnum): number | null => {
    if (fraction === null) {
        return null;
    }
    return metric === OeeMetricEnum.AVAILABILITY
        ? Math.floor(fraction * 100 + FLOOR_TOLERANCE)
        : Math.round(fraction * 100);
};

export const filterRowsByProductionLine = <T extends { productionLineId: number }>(
    rows: T[],
    productionLineId: number | null,
): T[] => (productionLineId === null ? rows : rows.filter((row) => row.productionLineId === productionLineId));

const groupBy = <T, K>(rows: readonly T[], keyOf: (row: T) => K): Map<K, T[]> =>
    rows.reduce((groups, row) => {
        const key = keyOf(row);
        groups.set(key, [...(groups.get(key) ?? []), row]);
        return groups;
    }, new Map<K, T[]>());

/** Per production line OEE, worst first; lines without planned time go last. */
export const groupOeeByProductionLine = (rows: readonly IOeeSummaryRow[]): IProductionLineOee[] =>
    Array.from(groupBy(rows, (row) => row.productionLineId).entries())
        .map(([productionLineId, lineRows]) => {
            const parts = sumOeeParts(lineRows);
            return {
                parts,
                productionLineId,
                productionLineName: lineRows[0].productionLineName,
                ratios: computeOeeRatios(parts),
            };
        })
        .sort((a, b) => {
            if (a.ratios.oee === b.ratios.oee) {
                return a.productionLineId - b.productionLineId;
            }
            if (a.ratios.oee === null) {
                return 1;
            }
            if (b.ratios.oee === null) {
                return -1;
            }
            return a.ratios.oee - b.ratios.oee;
        });

/**
 * Machine centers with the largest Performance or Quality loss (1 − factor, in
 * percent points). Machine centers where the factor is not measured (P null,
 * or no quality counts) or shows no loss are left out.
 */
export const topMachineCenterLosses = (
    rows: readonly IOeeSummaryRow[],
    factor: OeeMetricEnum.PERFORMANCE | OeeMetricEnum.QUALITY,
    limit: number = OEE_TOP_LIMIT,
): IMachineCenterLoss[] =>
    Array.from(groupBy(rows, (row) => row.machineCenterId).entries())
        .map(([machineCenterId, machineCenterRows]) => {
            const parts = sumOeeParts(machineCenterRows);
            const ratios = computeOeeRatios(parts);
            const measured =
                factor === OeeMetricEnum.PERFORMANCE
                    ? ratios.performance
                    : parts.totalCount > 0
                      ? ratios.quality
                      : null;
            return {
                lossPercent: measured === null ? 0 : (1 - measured) * 100,
                machineCenterId,
                machineCenterName: machineCenterRows[0].machineCenterName,
                ratios,
            };
        })
        // (1 - measured) * 100 leaves floating-point dust at exactly 100 %, which would plot a 0.0 % bar:
        // hold it to the same tolerance the availability floor uses.
        .filter((loss) => loss.lossPercent > FLOOR_TOLERANCE)
        .sort((a, b) => b.lossPercent - a.lossPercent || a.machineCenterId - b.machineCenterId)
        .slice(0, limit);

/**
 * Days up to a month of data, then ISO weeks, then calendar months. The month grain is
 * unreachable from the UI today - the widest preset, Last 90 days, still buckets by week - and
 * is kept so a wider preset or a custom range cannot blow the chart up into hundreds of points.
 */
export const resolveOeeTrendGrain = (startDate: string, endDate: string): OeeTrendGrain => {
    const inclusiveDays =
        moment(endDate, PRODUCTION_DATE_FORMAT).diff(moment(startDate, PRODUCTION_DATE_FORMAT), "days") + 1;

    if (inclusiveDays <= DAY_GRAIN_MAX_DAYS) {
        return "day";
    }
    return inclusiveDays <= WEEK_GRAIN_MAX_DAYS ? "week" : "month";
};

/**
 * One trend point per bucket of the auto-selected grain (day, ISO week, calendar
 * month) from the range start to the range end, as a whole percent of the chosen
 * metric. The first bucket starts at the range start; buckets without rows or
 * planned production time stay in the series with a null value, so they plot as gaps.
 */
export const buildOeeTrendPoints = (
    rows: readonly IOeeSummaryRow[],
    metric: OeeMetricEnum,
    range: { endDate: string; startDate: string },
): IOeeTrendPoint[] => {
    const grain = resolveOeeTrendGrain(range.startDate, range.endDate);
    const rangeStart = moment(range.startDate, PRODUCTION_DATE_FORMAT);
    const rangeEnd = moment(range.endDate, PRODUCTION_DATE_FORMAT);
    const bucketUnit: moment.unitOfTime.StartOf = grain === "week" ? "isoWeek" : grain;

    const bucketOf = (day: moment.Moment): string =>
        moment.max(day.clone().startOf(bucketUnit), rangeStart).format(PRODUCTION_DATE_FORMAT);

    const rowsByBucket = groupBy(rows, (row) => bucketOf(moment(row.productionDate, PRODUCTION_DATE_FORMAT)));
    const points: IOeeTrendPoint[] = [];

    for (
        let cursor = rangeStart.clone();
        cursor.isSameOrBefore(rangeEnd, "day");
        cursor = cursor.clone().startOf(bucketUnit).add(1, grain)
    ) {
        const bucketStart = bucketOf(cursor);
        const parts = sumOeeParts(rowsByBucket.get(bucketStart) ?? []);
        points.push({ bucketStart, grain, value: toMetricPercent(computeOeeRatios(parts)[metric], metric) });
    }

    return points;
};

/**
 * Whole minutes for display, never 0. The app writes this rule inline in ~28 places as
 * `Math.round(seconds / 60) < 1 ? 1 : Math.round(seconds / 60)`, which floors every downtime
 * figure at one minute, zero included, so a sub-minute stop reads as a stop instead of vanishing.
 */
export const secondsToDisplayMinutes = (seconds: number): number => Math.max(1, Math.round(seconds / 60));

/**
 * The machine centers with the most downtime, each as a share of ALL downtime in
 * scope (so the top five sum to less than 100 % when more machine centers
 * stopped), split by failure type.
 */
export const buildDowntimeTopFive = (
    rows: readonly IOeeDowntimeRow[],
    limit: number = OEE_TOP_LIMIT,
): IDowntimeTopFive => {
    const totalSeconds = rows.reduce((total, row) => total + row.downtimeSeconds, 0);

    if (totalSeconds <= 0) {
        return { bars: [], totalSeconds: 0 };
    }

    const bars = Array.from(groupBy(rows, (row) => row.machineCenterId).entries()).map(
        ([machineCenterId, machineCenterRows]) => {
            const segments = Array.from(groupBy(machineCenterRows, (row) => row.failureTypeId).entries())
                .map(([failureTypeId, failureTypeRows]) => {
                    const seconds = failureTypeRows.reduce((total, row) => total + row.downtimeSeconds, 0);
                    return {
                        failureTypeId,
                        minutes: secondsToDisplayMinutes(seconds),
                        percentOfTotal: (seconds / totalSeconds) * 100,
                        seconds,
                    };
                })
                .sort((a, b) => a.failureTypeId - b.failureTypeId);
            const seconds = segments.reduce((total, segment) => total + segment.seconds, 0);

            return { machineCenterId, percentOfTotal: (seconds / totalSeconds) * 100, seconds, segments };
        },
    );

    return {
        bars: bars.sort((a, b) => b.seconds - a.seconds || a.machineCenterId - b.machineCenterId).slice(0, limit),
        totalSeconds,
    };
};
